import { useState } from "react";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} QuestItem
 * @property {number} quest_id - ID of the quest
 * @property {string} quest_name - Name of the quest
 * @property {string} episodes - Episodes completed (e.g., "0/5")
 * @property {number} percentage - Completion percentage
 * @property {string|null} due_date - Due date
 * @property {string} status - Status of the quest (e.g., "Pending")
 */

/**
 * @typedef {object} StudentDetail
 * @property {number} id - Student ID
 * @property {string} name - Student name
 * @property {string} email - Student email
 * @property {string} username - Student username
 * @property {string} age - Student age
 * @property {string} student_code - Student code
 * @property {number} overall_progress - Overall progress percentage
 * @property {number} avg_quiz_score - Average quiz score
 * @property {number} total_quests_assigned - Total quests assigned
 */

/**
 * @typedef {Object<string, QuestItem[]>} QuestsByCategory
 */

/**
 * @typedef {object} StudentReportByIdData
 * @property {StudentDetail} student - Student details
 * @property {QuestsByCategory} quests_by_category - Quests grouped by category
 */

export const useGetStudentReportById = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
        /** @type {StudentReportByIdData | null | undefined} */(undefined)
    );

    /**
     * Fetch student report by ID
     * @async
     * @param {number|string} studentId - Student ID to fetch report for
     * @returns {Promise<void>}
     */
    const getStudentReportById = async (studentId) => {
        if (!studentId) {
            setError("Student ID is required");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const url = `${TENANT_DOMAIN}/school/admin/reports/students/${studentId}`;
            const response = await axiosInstance.get(url);

            const data = response.data;

            setReportData(data);
        } catch (err) {
            setReportData(null);
            setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the state
     */
    const resetState = () => {
        setReportData(undefined);
        setError("");
        setLoading(false);
    };

    return { loading, error, reportData, getStudentReportById, resetState };
};